/**
 * The seam between MSAL (which only exists inside React) and api.ts (a plain module
 * whose call sites take no arguments). A module-level registration rather than a
 * threaded parameter, so adding an endpoint cannot forget to authenticate it.
 *
 * The invariant this file exists to protect: **nothing registered means no
 * Authorization header at all.** `AUTH_MODE=mock` registers nothing, so a mock
 * deployment sends bare requests exactly as it did before M2. Defaulting to some
 * placeholder token instead would make every developer's offline setup depend on a
 * tenant, and would let a misconfigured deployment present an unauthenticated app as
 * working.
 */

export interface RequestAuth {
  /**
   * Bearer value for the next request. Called per request, not captured once: a
   * long-lived tab's cached token expires, and MSAL's own cache makes the repeat call
   * cheap while transparently renewing when the token is close to expiry.
   */
  token(): Promise<string>;
  /**
   * A 401 arrived despite a token MSAL considered valid — the API rejected it, so the
   * cache is wrong about our session. Returns true if interactive re-auth was started,
   * false if it must not be (see ReauthGuard: a 401 a fresh sign-in cannot fix would
   * otherwise loop forever).
   */
  recover(): Promise<boolean>;
  /** A request the API accepted. Re-arms the one-shot re-auth guard. */
  accepted(): void;
}

let registered: RequestAuth | null = null;

export function setRequestAuth(auth: RequestAuth | null): void {
  registered = auth;
}

export function getRequestAuth(): RequestAuth | null {
  return registered;
}
